import axios from 'axios';
import type { User } from 'firebase/auth';
import { useEffect, useRef } from 'react';

/**
 * Hook to configure axios interceptors for authentication and error handling
 *
 * Sets up:
 * - Request interceptor: Adds Firebase auth token to all requests
 * - Response interceptor: Global error handling for all API responses
 *
 * @param user - Firebase user object (null if not authenticated)
 */
export const useAxiosInterceptors = (user: User | null) => {
	const requestInterceptorIdRef = useRef<number | null>(null);
	const responseInterceptorIdRef = useRef<number | null>(null);

	useEffect(() => {
		// Remove previous interceptors if they exist
		if (requestInterceptorIdRef.current !== null) {
			axios.interceptors.request.eject(requestInterceptorIdRef.current);
		}
		if (responseInterceptorIdRef.current !== null) {
			axios.interceptors.response.eject(responseInterceptorIdRef.current);
		}

		// Add request interceptor for auth token
		const requestInterceptorId = axios.interceptors.request.use(
			async config => {
				// Check if request requires authentication (default: true)
				const requiresAuth = config.headers?.['X-Requires-Auth'] !== 'false';

				if (requiresAuth && !user) {
					// Reject requests that require auth when no user is logged in
					return Promise.reject(new Error('Authentication required'));
				}

				if (user) {
					try {
						const token = await user.getIdToken();
						config.headers.Authorization = `Bearer ${token}`;
					} catch (error) {
						console.error('Error getting auth token:', error);
						return Promise.reject(error);
					}
				}

				// Remove the custom header so it's not sent to the server
				delete config.headers?.['X-Requires-Auth'];

				return config;
			},
			error => {
				return Promise.reject(error);
			},
		);

		// Add response interceptor for error handling
		const responseInterceptorId = axios.interceptors.response.use(
			response => {
				// Return successful responses as-is
				return response;
			},
			error => {
				// Handle common error cases
				if (axios.isAxiosError(error)) {
					const status = error.response?.status;
					const errorMessage = error.response?.data?.error || error.message;

					switch (status) {
						case 401:
							console.error('Authentication error:', errorMessage);
							// Optionally redirect to login or refresh token
							break;
						case 403:
							console.error('Authorization error:', errorMessage);
							break;
						case 404:
							console.error('Resource not found:', errorMessage);
							break;
						case 500:
							console.error('Server error:', errorMessage);
							break;
						default:
							console.error('Request error:', errorMessage);
					}
				} else {
					console.error('Unexpected error:', error);
				}

				// Always reject so the calling code can handle it
				return Promise.reject(error);
			},
		);

		requestInterceptorIdRef.current = requestInterceptorId;
		responseInterceptorIdRef.current = responseInterceptorId;

		// Cleanup: eject interceptors when component unmounts or user changes
		return () => {
			if (requestInterceptorIdRef.current !== null) {
				axios.interceptors.request.eject(requestInterceptorIdRef.current);
				requestInterceptorIdRef.current = null;
			}
			if (responseInterceptorIdRef.current !== null) {
				axios.interceptors.response.eject(responseInterceptorIdRef.current);
				responseInterceptorIdRef.current = null;
			}
		};
	}, [user]);
};
